import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  QrCode, ShoppingBag, CreditCard, MessageSquare, Bot,
  ArrowLeft, ArrowRight, Globe, ChevronDown, ChevronUp
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_08f655cb-8b91-4c06-80bd-b94d5f4523ca/artifacts/kxmzqj5h_ChatGPT%20Image%2027%20de%20fev.%20de%202025%2C%2001_06_03.png";

function detectLang() {
  const lang = (navigator.language || navigator.userLanguage || "es").toLowerCase();
  if (lang.startsWith("pt")) return "pt";
  if (lang.startsWith("en")) return "en";
  return "es";
}

const T = {
  es: {
    back: "Volver",
    title: "Como Funciona",
    subtitle: "Pedir es facil, rapido y sin necesidad de descargar apps.",
    steps: [
      {
        num: "1",
        icon: QrCode,
        title: "Escanea el QR de la mesa",
        desc: "Abre la camara de tu telefono y escanea el codigo QR que esta en tu mesa. El menu digital se abre automaticamente en tu navegador.",
        tip: "No necesitas descargar ninguna app."
      },
      {
        num: "2",
        icon: ShoppingBag,
        title: "Elige tus productos",
        desc: "Navega por las categorias, ve las fotos y descripciones. Toca en los productos que quieras y ajusta las cantidades.",
        tip: "Puedes personalizar cada producto con observaciones."
      },
      {
        num: "3",
        icon: CreditCard,
        title: "Haz tu pedido y paga",
        desc: "Revisa tu carrito, elige la forma de pago (tarjeta, Bizum, MBWay o pago en caja) y confirma. Tu pedido va directo a la cocina.",
        tip: "El pago online es 100% seguro."
      },
      {
        num: "4",
        icon: MessageSquare,
        title: "Sigue tu pedido por WhatsApp",
        desc: "Recibiras notificaciones en cada etapa: pedido recibido, en preparacion, listo. Si es delivery, puedes rastrear al repartidor en tiempo real.",
        tip: "No necesitas estar atento, te avisamos por WhatsApp."
      },
    ],
    aiNote: {
      icon: Bot,
      title: "Dudas? Habla con nuestro Agente IA 24/7",
      desc: "En el menu digital, busca el boton de chat IA. Nuestro asistente inteligente esta disponible las 24 horas para responder tus dudas, ayudarte con el pedido o resolver cualquier problema.",
      cta: "Esta en el menu, siempre listo para ayudarte."
    },
    faq: {
      title: "Preguntas Frecuentes",
      items: [
        { q: "Necesito descargar alguna app?", a: "No. Todo funciona directamente en el navegador de tu telefono." },
        { q: "Es seguro pagar online?", a: "Si. Usamos Stripe, el mismo sistema de pago de Netflix y Amazon. Tus datos estan protegidos." },
        { q: "Puedo pedir con el restaurante cerrado?", a: "Si. Puedes programar tu pedido para cuando el restaurante abra." },
        { q: "Y si me equivoco en el pedido?", a: "Habla con el Agente IA en el menu o contacta al restaurante. Resolvemos rapidamente." },
      ]
    },
    cta: "Ver el Menu",
    ctaHome: "Volver al Inicio"
  },
  pt: {
    back: "Voltar",
    title: "Como Funciona",
    subtitle: "Pedir e facil, rapido e sem precisar baixar apps.",
    steps: [
      {
        num: "1",
        icon: QrCode,
        title: "Escaneie o QR da mesa",
        desc: "Abra a camera do seu celular e escaneie o codigo QR que esta na sua mesa. O cardapio digital abre automaticamente no seu navegador.",
        tip: "Nao precisa baixar nenhum app."
      },
      {
        num: "2",
        icon: ShoppingBag,
        title: "Escolha seus produtos",
        desc: "Navegue pelas categorias, veja as fotos e descricoes. Toque nos produtos que quiser e ajuste as quantidades.",
        tip: "Voce pode personalizar cada produto com observacoes."
      },
      {
        num: "3",
        icon: CreditCard,
        title: "Faca seu pedido e pague",
        desc: "Revise seu carrinho, escolha a forma de pagamento (cartao, MBWay, Bizum ou pagamento no caixa) e confirme. Seu pedido vai direto para a cozinha.",
        tip: "O pagamento online e 100% seguro."
      },
      {
        num: "4",
        icon: MessageSquare,
        title: "Acompanhe pelo WhatsApp",
        desc: "Voce recebera notificacoes em cada etapa: pedido recebido, em preparo, pronto. Se for delivery, pode rastrear o entregador em tempo real.",
        tip: "Nao precisa ficar atento, avisamos pelo WhatsApp."
      },
    ],
    aiNote: {
      icon: Bot,
      title: "Duvidas? Fale com nosso Agente IA 24/7",
      desc: "No cardapio digital, procure o botao de chat IA. Nosso assistente inteligente esta disponivel 24 horas para responder suas duvidas, ajudar com o pedido ou resolver qualquer problema.",
      cta: "Esta no cardapio, sempre pronto para te ajudar."
    },
    faq: {
      title: "Perguntas Frequentes",
      items: [
        { q: "Preciso baixar algum app?", a: "Nao. Tudo funciona diretamente no navegador do seu celular." },
        { q: "E seguro pagar online?", a: "Sim. Usamos Stripe, o mesmo sistema de pagamento da Netflix e Amazon. Seus dados estao protegidos." },
        { q: "Posso pedir com o restaurante fechado?", a: "Sim. Voce pode agendar seu pedido para quando o restaurante abrir." },
        { q: "E se eu errar no pedido?", a: "Fale com o Agente IA no cardapio ou contate o restaurante. Resolvemos rapidamente." },
      ]
    },
    cta: "Ver o Cardapio",
    ctaHome: "Voltar ao Inicio"
  },
  en: {
    back: "Back",
    title: "How It Works",
    subtitle: "Ordering is easy, fast, and no app download needed.",
    steps: [
      {
        num: "1",
        icon: QrCode,
        title: "Scan the table QR code",
        desc: "Open your phone camera and scan the QR code on your table. The digital menu opens automatically in your browser.",
        tip: "No app download required."
      },
      {
        num: "2",
        icon: ShoppingBag,
        title: "Choose your products",
        desc: "Browse categories, see photos and descriptions. Tap on products you want and adjust quantities.",
        tip: "You can customize each product with special notes."
      },
      {
        num: "3",
        icon: CreditCard,
        title: "Place your order and pay",
        desc: "Review your cart, choose payment method (card, Bizum, MBWay or pay at counter) and confirm. Your order goes straight to the kitchen.",
        tip: "Online payment is 100% secure."
      },
      {
        num: "4",
        icon: MessageSquare,
        title: "Track via WhatsApp",
        desc: "You'll receive notifications at every step: order received, preparing, ready. For delivery, you can track your driver in real time.",
        tip: "No need to keep checking, we'll notify you via WhatsApp."
      },
    ],
    aiNote: {
      icon: Bot,
      title: "Questions? Talk to our 24/7 AI Agent",
      desc: "In the digital menu, look for the AI chat button. Our smart assistant is available 24 hours to answer your questions, help with your order, or solve any issues.",
      cta: "It's in the menu, always ready to help you."
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        { q: "Do I need to download an app?", a: "No. Everything works directly in your phone's browser." },
        { q: "Is it safe to pay online?", a: "Yes. We use Stripe, the same payment system as Netflix and Amazon. Your data is protected." },
        { q: "Can I order when the restaurant is closed?", a: "Yes. You can schedule your order for when the restaurant opens." },
        { q: "What if I make a mistake in my order?", a: "Talk to the AI Agent in the menu or contact the restaurant. We'll resolve it quickly." },
      ]
    },
    cta: "View Menu",
    ctaHome: "Back to Home"
  }
};

export default function ComoFuncionaPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => detectLang());
  const [openFaq, setOpenFaq] = useState(null);
  const t = T[lang];

  const toggleLang = () => setLang(prev => prev === "es" ? "pt" : prev === "pt" ? "en" : "es");
  const nextLangLabel = { es: "PT", pt: "EN", en: "ES" };

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="como-funciona-page">
      {/* Header */}
      <nav className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#CDA34F] transition-colors" data-testid="como-funciona-back">
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Kaiso" className="w-7 h-7 rounded-lg" />
            <span className="font-bold text-[#CDA34F] text-sm">Kaiso System</span>
          </div>
          <button onClick={toggleLang} className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-[#CDA34F] border border-gray-200 px-2 py-1 rounded-lg" data-testid="como-funciona-lang">
            <Globe className="w-3.5 h-3.5" />
            {nextLangLabel[lang]}
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-12">
        {/* Title */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900" data-testid="como-funciona-title">{t.title}</h1>
          <p className="text-base text-gray-500 max-w-md mx-auto">{t.subtitle}</p>
        </div>

        {/* Steps */}
        <div className="space-y-6">
          {t.steps.map((step, i) => (
            <div key={i} className="relative" data-testid={`como-funciona-step-${i}`}>
              {/* Connector line */}
              {i < t.steps.length - 1 && (
                <div className="absolute left-7 top-[72px] w-0.5 h-[calc(100%-32px)] bg-[#CDA34F]/20" />
              )}
              <div className="flex gap-5">
                {/* Step number circle */}
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-[#CDA34F] flex items-center justify-center shadow-lg shadow-[#CDA34F]/20">
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                {/* Content */}
                <div className="flex-1 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-black text-[#CDA34F] bg-[#CDA34F]/10 px-2.5 py-0.5 rounded-full">{lang === "en" ? "STEP" : "PASO"} {step.num}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{step.desc}</p>
                  <p className="text-xs text-[#CDA34F] font-semibold flex items-center gap-1.5">
                    <ArrowRight className="w-3 h-3" />
                    {step.tip}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* AI Agent Note - IMPORTANT */}
        <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 text-center space-y-4" data-testid="ai-agent-note">
          <div className="w-14 h-14 rounded-2xl bg-[#CDA34F]/20 flex items-center justify-center mx-auto">
            <Bot className="w-8 h-8 text-[#CDA34F]" />
          </div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-white">{t.aiNote.title}</h2>
          <p className="text-sm text-gray-400 leading-relaxed max-w-lg mx-auto">{t.aiNote.desc}</p>
          <p className="text-[#CDA34F] font-bold text-sm">{t.aiNote.cta}</p>
        </div>

        {/* FAQ */}
        <div className="space-y-4" data-testid="como-funciona-faq">
          <h2 className="text-xl font-extrabold text-gray-900 text-center">{t.faq.title}</h2>
          <div className="space-y-2">
            {t.faq.items.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left"
                  data-testid={`faq-item-${i}`}
                >
                  <span className="text-sm font-semibold text-gray-800 pr-4">{item.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center space-y-3 pb-8">
          <Button onClick={() => navigate("/")} className="h-12 px-8 bg-[#CDA34F] hover:bg-[#b8903e] text-white font-bold uppercase tracking-wide rounded-lg" data-testid="como-funciona-cta-home">
            {t.ctaHome}
          </Button>
        </div>
      </div>
    </div>
  );
}
